import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';

interface PaymentModeDetails {
  transaction_id?: string | null; upi_id?: string | null;
  reference_number?: string | null; cheque_number?: string | null;
  bank_name?: string | null; cheque_date?: string | null;
}

interface Payment {
  payment_id: string;
  amount_paid: number;
  discount_amount: number;
  discount_reason: string | null;
  payment_method: string;
  payment_mode_details: PaymentModeDetails | null;
  receipt_number: string | null;
  payment_date: string | null;
  status: string;
  remarks: string | null;
  created_at: string | null;
}

interface Student  { student_id: string; name: string | null; email: string | null; mobile: string | null; }
interface Enrollment {
  enrollment_id: string; course_id: string | null; course_name: string | null;
  course_title: string | null; batch_id: string | null; batch_name: string | null;
  batch_time: string | null; date_of_admission: string | null;
}
interface FeeStructure { base_fee: number; additional_fee: number; total_fee: number; }
interface Summary {
  total_fee: number; total_paid: number; total_discount: number; balance_due: number;
  payment_count: number; completed_payments: number; pending_payments: number;
  last_payment_date: string | null; payment_status: string;
}

interface PaymentHistoryModalProps {
  visible: boolean; studentId: string; enrollmentId: string;
  studentName?: string; courseName?: string; onHide: () => void;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const getStatusStyle = (status: string): { bg: string; color: string } => ({
  completed: { bg: '#f0fdf4', color: '#15803d' },
  pending:   { bg: 'rgba(251,178,31,0.12)', color: '#92580a' },
  failed:    { bg: '#fef2f2', color: '#dc2626' },
  refunded:  { bg: 'rgba(14,34,93,0.08)',   color: C.navy },
}[status] || { bg: C.bg, color: C.sub });

const methodLabel: Record<string, string> = {
  cash: 'Cash', upi: 'UPI', card: 'Card', bank_transfer: 'Bank Transfer', cheque: 'Cheque',
};

const colHeaderStyle: React.CSSProperties = {
  background: C.bg, color: C.muted, fontSize: '0.68rem', fontWeight: '700',
  textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: `2px solid ${C.border}`, padding: '12px 14px',
};
const colBodyStyle: React.CSSProperties = { padding: '14px' };

const PaymentHistoryModal: React.FC<PaymentHistoryModalProps> = ({
  visible, studentId, enrollmentId, studentName, courseName, onHide,
}) => {
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [student, setStudent]           = useState<Student | null>(null);
  const [enrollment, setEnrollment]     = useState<Enrollment | null>(null);
  const [feeStructure, setFeeStructure] = useState<FeeStructure | null>(null);
  const [summary, setSummary]           = useState<Summary | null>(null);
  const [payments, setPayments]         = useState<Payment[]>([]);

  useEffect(() => {
    if (visible && studentId && enrollmentId) fetchPaymentHistory();
  }, [visible, studentId, enrollmentId]);

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true); setError(null);
      const res = await apiClient.get(`/v1/fees/payment-history/${studentId}/${enrollmentId}`);
      if (res.data.success) {
        const d = res.data.data;
        setStudent(d.student); setEnrollment(d.enrollment);
        setFeeStructure(d.fee_structure); setSummary(d.summary); setPayments(d.payments);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load payment history');
    } finally { setLoading(false); }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const formatDateTime = (d: string | null) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  // derived
  const hasDiscounts = payments.some(p => (p.discount_amount || 0) > 0);
  const totalDiscount = payments.reduce((s, p) => s + (p.discount_amount || 0), 0);

  // ── Column templates ───────────────────────────────────────────────────────
  const amountTemplate = (row: Payment) => (
    <div>
      <span style={{ fontSize: '0.92rem', fontWeight: '700', color: C.navy }}>
        ₹{row.amount_paid.toLocaleString('en-IN')}
      </span>
    </div>
  );

  const discountTemplate = (row: Payment) => {
    if (!row.discount_amount || row.discount_amount === 0) {
      return <span style={{ fontSize: '0.8rem', color: C.muted, fontStyle: 'italic' }}>—</span>;
    }
    return (
      <div>
        <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: '#92580a' }}>
          ₹{row.discount_amount.toLocaleString('en-IN')}
        </p>
        {row.discount_reason && (
          <p style={{ margin: 0, fontSize: '0.72rem', color: C.muted, lineHeight: '1.4' }}>
            {row.discount_reason}
          </p>
        )}
      </div>
    );
  };

  const methodTemplate = (row: Payment) => (
    <div>
      <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: C.navy }}>
        {methodLabel[row.payment_method] || row.payment_method}
      </p>
      {row.payment_mode_details && (
        <div style={{ fontSize: '0.72rem', color: C.muted, lineHeight: '1.6' }}>
          {row.payment_mode_details.transaction_id  && <div>TXN: {row.payment_mode_details.transaction_id}</div>}
          {row.payment_mode_details.upi_id           && <div>UPI: {row.payment_mode_details.upi_id}</div>}
          {row.payment_mode_details.cheque_number    && <div>Cheque: {row.payment_mode_details.cheque_number}</div>}
          {row.payment_mode_details.reference_number && <div>Ref: {row.payment_mode_details.reference_number}</div>}
        </div>
      )}
    </div>
  );

  const dateTemplate = (row: Payment) => (
    <div>
      <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: C.navy }}>
        {formatDate(row.payment_date)}
      </p>
      <p style={{ margin: 0, fontSize: '0.72rem', color: C.muted, fontFamily: 'monospace' }}>
        {row.receipt_number || '-'}
      </p>
    </div>
  );

  const statusTemplate = (row: Payment) => {
    const s = getStatusStyle(row.status);
    return (
      <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', textTransform: 'capitalize', background: s.bg, color: s.color, border: `1px solid ${row.status === 'pending' ? C.goldBorder : 'transparent'}` }}>
        {row.status}
      </span>
    );
  };

  const remarksTemplate = (row: Payment) => (
    <span style={{ fontSize: '0.8rem', color: C.sub, fontStyle: row.remarks ? 'normal' : 'italic' }}>
      {row.remarks || '—'}
    </span>
  );

  // ── Summary stat cards ─────────────────────────────────────────────────────
  const statCards = summary ? [
    {
      label: 'Total Fee',
      value: `₹${summary.total_fee.toLocaleString('en-IN')}`,
      icon: 'pi-indian-rupee',
      bg: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
      accent: C.gold
    },
    {
      label: 'Paid',
      value: `₹${summary.total_paid.toLocaleString('en-IN')}`,
      icon: 'pi-check-circle',
      bg: 'linear-gradient(135deg, #166534 0%, #22c55e 100%)',
      accent: '#86efac'
    },
    ...(totalDiscount > 0 ? [{
      label: 'Discount',
      value: `₹${totalDiscount.toLocaleString('en-IN')}`,
      icon: 'pi-tag',
      bg: 'linear-gradient(135deg, #92580a 0%, #b8860b 100%)',
      accent: '#fde68a'
    }] : []),
    {
      label: 'Balance',
      value: `₹${summary.balance_due.toLocaleString('en-IN')}`,
      icon: summary.balance_due > 0 ? 'pi-exclamation-triangle' : 'pi-check',
      bg: summary.balance_due > 0
        ? 'linear-gradient(135deg, #991b1b 0%, #ef4444 100%)'
        : 'linear-gradient(135deg, #166534 0%, #22c55e 100%)',
      accent: summary.balance_due > 0 ? '#fca5a5' : '#86efac',
    },
    {
      label: 'Completed',
      value: String(summary.completed_payments),
      icon: 'pi-list',
      bg: `linear-gradient(135deg, ${C.navyMid} 0%, ${C.navy} 100%)`,
      accent: C.gold
    },
  ] : [];

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: '90vw', maxWidth: '960px' }}
      dismissableMask
      draggable={false}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: '24px' } },
      }}
    >
      <div>
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '16px', padding: '28px', marginBottom: '24px',
          color: C.white, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '180px', height: '180px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-30px', left: '25%', width: '120px', height: '120px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
              <i className="pi pi-credit-card" style={{ fontSize: '0.7rem' }} />
              Payment History
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.5rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
              {studentName || student?.name || 'Student'}
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
              {courseName || enrollment?.course_name || 'Course'}
              {enrollment?.batch_name ? ` · ${enrollment.batch_name}` : ''}
              {enrollment?.batch_time ? ` (${enrollment.batch_time})` : ''}
            </p>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />}

        {/* ── Loading ─────────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 20px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
            <div style={{ textAlign: 'center' }}>
              <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
              <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading payment history...</p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Summary Cards ──────────────────────────────────────── */}
            {summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                {statCards.map(card => (
                  <div key={card.label} style={{ background: card.bg, borderRadius: '14px', padding: '18px 16px', color: C.white, position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: '-10px', right: '-10px', width: '60px', height: '60px', background: 'rgba(255,255,255,0.07)', borderRadius: '50%', pointerEvents: 'none' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                      <i className={`pi ${card.icon}`} style={{ fontSize: '0.8rem', color: card.accent }} />
                      <span style={{ fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.75 }}>{card.label}</span>
                    </div>
                    <div style={{ fontSize: '1.35rem', fontWeight: '700', letterSpacing: '-0.5px' }}>{card.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Payments Table ─────────────────────────────────────── */}
            <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', marginBottom: '16px' }}>
              {/* Table header row */}
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, background: C.bg, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '30px', height: '30px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-list" style={{ color: C.gold, fontSize: '0.8rem' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: '700', color: C.navy }}>
                  Payment Transactions
                  {payments.length > 0 && (
                    <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '20px', background: C.goldDim, color: '#92580a', fontSize: '0.7rem', fontWeight: '700', border: `1px solid ${C.goldBorder}` }}>
                      {payments.length}
                    </span>
                  )}
                </h3>
              </div>

              {payments.length > 0 ? (
                <DataTable value={payments} dataKey="payment_id" emptyMessage="No payments found" style={{ fontSize: '0.85rem' }}>
                  <Column field="amount_paid"    header="Amount"          body={amountTemplate}   headerStyle={colHeaderStyle} bodyStyle={colBodyStyle} />
                  {hasDiscounts && (
                    <Column field="discount_amount" header="Discount"     body={discountTemplate} headerStyle={{ ...colHeaderStyle, color: '#92580a' }} bodyStyle={colBodyStyle} />
                  )}
                  <Column field="payment_method" header="Method"          body={methodTemplate}   headerStyle={colHeaderStyle} bodyStyle={colBodyStyle} />
                  <Column field="payment_date"   header="Date & Receipt"  body={dateTemplate}     headerStyle={colHeaderStyle} bodyStyle={colBodyStyle} />
                  <Column field="status"         header="Status"          body={statusTemplate}   headerStyle={colHeaderStyle} bodyStyle={colBodyStyle} />
                  <Column field="remarks"        header="Remarks"         body={remarksTemplate}  headerStyle={colHeaderStyle} bodyStyle={colBodyStyle} />
                </DataTable>
              ) : (
                <div style={{ padding: '60px 20px', textAlign: 'center', background: C.bg }}>
                  <div style={{ width: '52px', height: '52px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <i className="pi pi-inbox" style={{ fontSize: '1.5rem', color: C.navy, opacity: 0.25 }} />
                  </div>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>No Payment History</p>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: C.muted }}>No payments have been recorded for this enrollment yet.</p>
                </div>
              )}
            </div>

            {/* ── Last Payment Info bar ──────────────────────────────── */}
            {summary?.last_payment_date && (
              <div style={{ padding: '12px 16px', background: C.bg, borderRadius: '10px', border: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: C.sub, fontWeight: '600' }}>
                  <i className="pi pi-clock" style={{ color: C.gold, fontSize: '0.8rem' }} />
                  Last payment: {formatDateTime(summary.last_payment_date)}
                </span>
                <span style={{
                  padding: '4px 12px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700',
                  ...(summary.payment_status === 'fully_paid'
                    ? { background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac' }
                    : summary.payment_status === 'partial'
                    ? { background: C.goldDim, color: '#92580a', border: `1px solid ${C.goldBorder}` }
                    : { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' })
                }}>
                  {summary.payment_status === 'fully_paid' ? '✓ Fully Paid' : summary.payment_status === 'partial' ? '⚠ Partially Paid' : '✗ Not Paid'}
                </span>
              </div>
            )}
          </>
        )}

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onHide}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 28px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.2)', transition: 'all 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)'; }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.85rem' }} />
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default PaymentHistoryModal;